import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuctionCard from "@/components/AuctionCard";
import BrandCard from "@/components/BrandCard";
import LandCard from "@/components/LandCard";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

function SectionHead({ eyebrow, title, sub, to, cta, testid }) {
    return (
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{eyebrow}</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">{title}</h2>
                <p className="text-black/60 mt-4 max-w-xl leading-relaxed">{sub}</p>
            </div>
            <Link to={to} data-testid={testid} className="inline-flex items-center gap-2 border border-black text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-white transition-colors">
                {cta} <ArrowRight size={12}/>
            </Link>
        </div>
    );
}

export function FeaturedAuctions() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/properties", { params: { listing_type: "auction", limit: 100 } })
            .then((r) => {
                const up = (r.data.items || []).filter((x) => x.auction_status === "upcoming")
                    .sort((a, b) => (b.featured - a.featured) || String(a.auction_date || "").localeCompare(String(b.auction_date || "")));
                setItems(up.slice(0, 4));
            })
            .catch(() => {});
    }, []);
    if (!items.length) return null;
    return (
        <section data-testid="home-auctions" className="bg-[var(--ebg-ivory)]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                <SectionHead eyebrow="Auction Properties" title="Upcoming auctions." sub="Bank and authority auctions, reviewed by our legal desk before you bid." to="/auction-properties" cta="View All Auctions" testid="home-auctions-cta"/>
                <div className={`grid sm:grid-cols-2 gap-6 ${items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
                    {items.map((p) => <AuctionCard key={p.id} item={p}/>)}
                </div>
            </div>
        </section>
    );
}

export function FeaturedBrands() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/brands", { params: { featured: true, limit: 6 } }).then((r) => setItems(r.data.items || [])).catch(() => {});
    }, []);
    if (!items.length) return null;
    return (
        <section data-testid="home-brands" className="bg-white">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                <SectionHead eyebrow="Brand for Commercial" title="Featured brands." sub="Franchise, lease and partnership opportunities for commercial space owners and investors." to="/brands" cta="View All Brands" testid="home-brands-cta"/>
                <div className={`grid sm:grid-cols-2 gap-6 ${items.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
                    {items.map((b) => <BrandCard key={b.id} brand={b}/>)}
                </div>
            </div>
        </section>
    );
}

export function FeaturedLand() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/properties", { params: { land: true, featured: true, limit: 8 } }).then((r) => setItems((r.data.items || []).slice(0, 4))).catch(() => {});
    }, []);
    if (!items.length) return null;
    return (
        <section data-testid="home-land" className="bg-white">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                <SectionHead eyebrow="Land Opportunities" title="Land, ready for what's next." sub="Residential • Commercial • Agricultural • Development • Investment" to="/land" cta="Explore Land Properties" testid="home-land-cta"/>
                <div className={`grid sm:grid-cols-2 gap-6 ${items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
                    {items.map((p) => <LandCard key={p.id} item={p}/>)}
                </div>
            </div>
        </section>
    );
}
